import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, Send, CheckCircle, Clock, Sparkles, ImagePlus, X, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AuthenticatedHeader } from '@/components/layout/AuthenticatedHeader';
import { Footer } from '@/components/layout/Footer';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useFounderStatus } from '@/hooks/useFounderStatus';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { toast } from 'sonner';

const copy = {
  en: {
    pageTitle: 'Share Your Idea',
    pageDesc: 'As a Founding Member, you have a direct line to shape the future of Say It NOW. Tell us what you\'d love to see.',
    titleLabel: 'What\'s your idea?',
    titlePlaceholder: 'e.g. Add birthday reminders for recipients',
    descLabel: 'Tell us more',
    descPlaceholder: 'Describe your idea in detail — what problem it solves, how it would help you or others, any specifics you have in mind...',
    categoryLabel: 'Category',
    categories: { general: 'General', feature: 'New Feature', improvement: 'Improvement', design: 'Design', content: 'Content & Templates' },
    submit: 'Submit Idea',
    submitting: 'Submitting...',
    success: 'Thank you! Your idea has been submitted and sent to the team.',
    error: 'Something went wrong. Please try again.',
    photosLabel: 'Attach screenshots or examples (optional)',
    photosHint: 'Upload up to 3 images to help illustrate your idea',
    previousIdeas: 'Your Previous Ideas',
    empty: 'No ideas submitted yet. Be the first!',
    statusLabels: { submitted: 'Submitted', reviewed: 'Under Review', planned: 'Planned', completed: 'Done' },
    adminResponse: 'Team Response',
    notFounder: 'This page is for Founding Members only.',
    backToDashboard: 'Back to Dashboard',
  },
  es: {
    pageTitle: 'Comparte Tu Idea',
    pageDesc: 'Como Miembro Fundador, tienes línea directa para dar forma al futuro de Say It NOW. Dinos qué te gustaría ver.',
    titleLabel: '¿Cuál es tu idea?',
    titlePlaceholder: 'ej. Añadir recordatorios de cumpleaños',
    descLabel: 'Cuéntanos más',
    descPlaceholder: 'Describe tu idea en detalle — qué problema resuelve, cómo ayudaría...',
    categoryLabel: 'Categoría',
    categories: { general: 'General', feature: 'Nueva Función', improvement: 'Mejora', design: 'Diseño', content: 'Contenido y Plantillas' },
    submit: 'Enviar Idea',
    submitting: 'Enviando...',
    success: '¡Gracias! Tu idea ha sido enviada al equipo.',
    error: 'Algo salió mal. Inténtalo de nuevo.',
    photosLabel: 'Adjuntar capturas o ejemplos (opcional)',
    photosHint: 'Sube hasta 3 imágenes para ilustrar tu idea',
    previousIdeas: 'Tus Ideas Anteriores',
    empty: 'Sin ideas aún. ¡Sé el primero!',
    statusLabels: { submitted: 'Enviada', reviewed: 'En Revisión', planned: 'Planeada', completed: 'Hecha' },
    adminResponse: 'Respuesta del Equipo',
    notFounder: 'Esta página es solo para Miembros Fundadores.',
    backToDashboard: 'Volver al Panel',
  },
  fr: {
    pageTitle: 'Partagez Votre Idée',
    pageDesc: 'En tant que Membre Fondateur, vous avez une ligne directe pour façonner l\'avenir de Say It NOW.',
    titleLabel: 'Quelle est votre idée ?',
    titlePlaceholder: 'ex. Ajouter des rappels d\'anniversaire',
    descLabel: 'Dites-nous en plus',
    descPlaceholder: 'Décrivez votre idée en détail...',
    categoryLabel: 'Catégorie',
    categories: { general: 'Général', feature: 'Nouvelle Fonction', improvement: 'Amélioration', design: 'Design', content: 'Contenu et Modèles' },
    submit: 'Soumettre l\'Idée',
    submitting: 'Envoi...',
    success: 'Merci ! Votre idée a été envoyée à l\'équipe.',
    error: 'Un problème est survenu. Réessayez.',
    photosLabel: 'Joindre des captures d\'écran (optionnel)',
    photosHint: 'Téléchargez jusqu\'à 3 images',
    previousIdeas: 'Vos Idées Précédentes',
    empty: 'Aucune idée pour le moment.',
    statusLabels: { submitted: 'Soumise', reviewed: 'En Examen', planned: 'Planifiée', completed: 'Fait' },
    adminResponse: 'Réponse de l\'Équipe',
    notFounder: 'Cette page est réservée aux Membres Fondateurs.',
    backToDashboard: 'Retour au Tableau de Bord',
  },
  pt: {
    pageTitle: 'Compartilhe Sua Ideia',
    pageDesc: 'Como Membro Fundador, você tem linha direta para moldar o futuro do Say It NOW.',
    titleLabel: 'Qual é a sua ideia?',
    titlePlaceholder: 'ex. Adicionar lembretes de aniversário',
    descLabel: 'Conte-nos mais',
    descPlaceholder: 'Descreva sua ideia em detalhes...',
    categoryLabel: 'Categoria',
    categories: { general: 'Geral', feature: 'Nova Função', improvement: 'Melhoria', design: 'Design', content: 'Conteúdo e Modelos' },
    submit: 'Enviar Ideia',
    submitting: 'Enviando...',
    success: 'Obrigado! Sua ideia foi enviada para a equipe.',
    error: 'Algo deu errado. Tente novamente.',
    photosLabel: 'Anexar capturas de tela (opcional)',
    photosHint: 'Envie até 3 imagens',
    previousIdeas: 'Suas Ideias Anteriores',
    empty: 'Nenhuma ideia ainda.',
    statusLabels: { submitted: 'Enviada', reviewed: 'Em Análise', planned: 'Planejada', completed: 'Feita' },
    adminResponse: 'Resposta da Equipe',
    notFounder: 'Esta página é apenas para Membros Fundadores.',
    backToDashboard: 'Voltar ao Painel',
  },
  ar: {
    pageTitle: 'شارك فكرتك',
    pageDesc: 'كعضو مؤسس، لديك خط مباشر لتشكيل مستقبل Say It NOW.',
    titleLabel: 'ما هي فكرتك؟',
    titlePlaceholder: 'مثال: إضافة تذكيرات أعياد الميلاد',
    descLabel: 'أخبرنا المزيد',
    descPlaceholder: 'صف فكرتك بالتفصيل...',
    categoryLabel: 'الفئة',
    categories: { general: 'عام', feature: 'ميزة جديدة', improvement: 'تحسين', design: 'تصميم', content: 'محتوى وقوالب' },
    submit: 'إرسال الفكرة',
    submitting: 'جارٍ الإرسال...',
    success: 'شكراً! تم إرسال فكرتك إلى الفريق.',
    error: 'حدث خطأ. حاول مرة أخرى.',
    photosLabel: 'إرفاق لقطات شاشة (اختياري)',
    photosHint: 'ارفع حتى 3 صور',
    previousIdeas: 'أفكارك السابقة',
    empty: 'لا توجد أفكار بعد.',
    statusLabels: { submitted: 'مُرسلة', reviewed: 'قيد المراجعة', planned: 'مُخططة', completed: 'مُكتملة' },
    adminResponse: 'رد الفريق',
    notFounder: 'هذه الصفحة مخصصة للأعضاء المؤسسين فقط.',
    backToDashboard: 'العودة إلى لوحة التحكم',
  },
  ja: {
    pageTitle: 'アイデアを共有',
    pageDesc: '創設メンバーとして、Say It NOWの未来を形作る直接の声があります。',
    titleLabel: 'あなたのアイデアは？',
    titlePlaceholder: '例：誕生日リマインダーを追加',
    descLabel: '詳しく教えてください',
    descPlaceholder: 'アイデアの詳細を説明してください...',
    categoryLabel: 'カテゴリー',
    categories: { general: '一般', feature: '新機能', improvement: '改善', design: 'デザイン', content: 'コンテンツとテンプレート' },
    submit: 'アイデアを送信',
    submitting: '送信中...',
    success: 'ありがとうございます！アイデアがチームに送信されました。',
    error: 'エラーが発生しました。もう一度お試しください。',
    photosLabel: 'スクリーンショットを添付（任意）',
    photosHint: '最大3枚の画像をアップロード',
    previousIdeas: '過去のアイデア',
    empty: 'まだアイデアはありません。',
    statusLabels: { submitted: '送信済み', reviewed: 'レビュー中', planned: '計画中', completed: '完了' },
    adminResponse: 'チームの回答',
    notFounder: 'このページは創設メンバー専用です。',
    backToDashboard: 'ダッシュボードに戻る',
  },
  ko: {
    pageTitle: '아이디어 공유',
    pageDesc: '창립 멤버로서 Say It NOW의 미래를 함께 만들어 주세요.',
    titleLabel: '당신의 아이디어는?',
    titlePlaceholder: '예: 생일 알림 추가',
    descLabel: '자세히 알려주세요',
    descPlaceholder: '아이디어를 자세히 설명해주세요...',
    categoryLabel: '카테고리',
    categories: { general: '일반', feature: '새 기능', improvement: '개선', design: '디자인', content: '콘텐츠 및 템플릿' },
    submit: '아이디어 제출',
    submitting: '제출 중...',
    success: '감사합니다! 아이디어가 팀에 전송되었습니다.',
    error: '문제가 발생했습니다. 다시 시도해주세요.',
    photosLabel: '스크린샷 첨부 (선택사항)',
    photosHint: '최대 3개 이미지 업로드',
    previousIdeas: '이전 아이디어',
    empty: '아직 아이디어가 없습니다.',
    statusLabels: { submitted: '제출됨', reviewed: '검토 중', planned: '계획됨', completed: '완료' },
    adminResponse: '팀 응답',
    notFounder: '이 페이지는 창립 멤버 전용입니다.',
    backToDashboard: '대시보드로 돌아가기',
  },
  id: {
    pageTitle: 'Bagikan Idemu',
    pageDesc: 'Sebagai Anggota Pendiri, kamu punya jalur langsung untuk membentuk masa depan Say It NOW.',
    titleLabel: 'Apa idemu?',
    titlePlaceholder: 'cth. Tambahkan pengingat ulang tahun',
    descLabel: 'Ceritakan lebih lanjut',
    descPlaceholder: 'Jelaskan idemu secara detail...',
    categoryLabel: 'Kategori',
    categories: { general: 'Umum', feature: 'Fitur Baru', improvement: 'Peningkatan', design: 'Desain', content: 'Konten & Template' },
    submit: 'Kirim Ide',
    submitting: 'Mengirim...',
    success: 'Terima kasih! Idemu telah dikirim ke tim.',
    error: 'Terjadi kesalahan. Coba lagi.',
    photosLabel: 'Lampirkan tangkapan layar (opsional)',
    photosHint: 'Unggah hingga 3 gambar',
    previousIdeas: 'Ide Sebelumnya',
    empty: 'Belum ada ide.',
    statusLabels: { submitted: 'Dikirim', reviewed: 'Ditinjau', planned: 'Direncanakan', completed: 'Selesai' },
    adminResponse: 'Tanggapan Tim',
    notFounder: 'Halaman ini hanya untuk Anggota Pendiri.',
    backToDashboard: 'Kembali ke Dasbor',
  },

  hi: {
    pageTitle: 'अपना विचार साझा करें',
    pageDesc: 'एक संस्थापक सदस्य के रूप में, आपके पास Say It NOW के भविष्य को आकार देने के लिए एक सीधी रेखा है। हमें बताएं कि आप क्या देखना पसंद करेंगे।',
    titleLabel: 'आपका विचार क्या है?',
    titlePlaceholder: 'उदाहरण के लिए: प्राप्तकर्ताओं के लिए जन्मदिन रिमाइंडर जोड़ें',
    descLabel: 'हमें और बताएं',
    descPlaceholder: 'अपने विचार का विस्तार से वर्णन करें — यह क्या समस्या हल करता है, यह आपको या दूसरों को कैसे मदद करेगा, आपके मन में कोई भी विशिष्टता...',
    categoryLabel: 'श्रेणी',
    categories: { general: 'सामान्य', feature: 'नई सुविधा', improvement: 'सुधार', design: 'डिज़ाइन', content: 'सामग्री और टेम्पलेट' },
    submit: 'विचार सबमिट करें',
    submitting: 'सबमिट किया जा रहा है...',
    success: 'धन्यवाद! आपका विचार प्रस्तुत कर दिया गया है और टीम को भेज दिया गया है।',
    error: 'कुछ गलत हो गया। कृपया पुनः प्रयास करें।',
    photosLabel: 'स्क्रीनशॉट या उदाहरण संलग्न करें (वैकल्पिक)',
    photosHint: 'अपने विचार को स्पष्ट करने में मदद करने के लिए अधिकतम 3 छवियां अपलोड करें',
    previousIdeas: 'आपके पिछले विचार',
    empty: 'अभी तक कोई विचार प्रस्तुत नहीं किया गया है। पहले बनें!',
    statusLabels: { submitted: 'सबमिट किया गया', reviewed: 'समीक्षाधीन', planned: 'योजनाबद्ध', completed: 'हो गया' },
    adminResponse: 'टीम की प्रतिक्रिया',
    notFounder: 'यह पृष्ठ केवल संस्थापक सदस्यों के लिए है।',
    backToDashboard: 'डैशबोर्ड पर वापस',
  },
};

interface Suggestion {
  id: string;
  category: string;
  title: string;
  description: string;
  status: string;
  admin_response: string | null;
  created_at: string;
}

export default function FounderSuggestions() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { isActiveFounder } = useFounderStatus();
  const t = lc(copy, language);

  useDocumentHead({
    title: `${t.pageTitle} | Say It NOW`,
    description: t.pageDesc,
  });

  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState<string[]>([]);

  const fetchSuggestions = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('founder_suggestions' as any)
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    if (data) setSuggestions(data as unknown as Suggestion[]);
  }, [user]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const handlePhotoAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remaining = 3 - photos.length;
    const newFiles = files.slice(0, remaining);
    
    setPhotos(prev => [...prev, ...newFiles]);
    
    newFiles.forEach(file => {
      const url = URL.createObjectURL(file);
      setPhotoPreviewUrls(prev => [...prev, url]);
    });
    
    // Reset input
    e.target.value = '';
  };

  const handlePhotoRemove = (index: number) => {
    URL.revokeObjectURL(photoPreviewUrls[index]);
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!user || !title.trim() || !description.trim()) return;
    setIsSubmitting(true);

    try {
      // Upload photos if any
      const uploadedUrls: string[] = [];
      for (const photo of photos) {
        const ext = photo.name.split('.').pop() || 'jpg';
        const filePath = `suggestions/${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        
        const { error: uploadError } = await supabase.storage
          .from('announcement-photos')
          .upload(filePath, photo, { contentType: photo.type });
        
        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from('announcement-photos')
            .getPublicUrl(filePath);
          uploadedUrls.push(urlData.publicUrl);
        }
      }

      // Save to database
      const { error } = await supabase
        .from('founder_suggestions' as any)
        .insert({
          user_id: user.id,
          title: title.trim(),
          description: description.trim(),
          category,
        });

      if (error) throw error;

      // Send email notification via edge function
      const userName = user.user_metadata?.full_name || user.email || 'Unknown';
      await supabase.functions.invoke('send-founder-suggestion-email', {
        body: {
          title: title.trim(),
          description: description.trim(),
          category,
          userName,
          userEmail: user.email,
          photoUrls: uploadedUrls,
        },
      });

      toast.success(t.success);
      setTitle('');
      setDescription('');
      setCategory('general');
      setPhotos([]);
      photoPreviewUrls.forEach(url => URL.revokeObjectURL(url));
      setPhotoPreviewUrls([]);
      fetchSuggestions();
    } catch (err) {
      console.error('Error submitting suggestion:', err);
      toast.error(t.error);
    }

    setIsSubmitting(false);
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-3.5 w-3.5" />;
      case 'planned': return <Sparkles className="h-3.5 w-3.5" />;
      default: return <Clock className="h-3.5 w-3.5" />;
    }
  };

  const statusVariant = (status: string): 'default' | 'secondary' | 'outline' => {
    switch (status) {
      case 'completed': return 'default';
      case 'planned': return 'default';
      case 'reviewed': return 'secondary';
      default: return 'outline';
    }
  };

  const catLabels = t.categories as Record<string, string>;
  const statLabels = t.statusLabels as Record<string, string>;

  if (!isActiveFounder) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <AuthenticatedHeader />
        <main className="flex-1 container py-16 text-center">
          <Lightbulb className="h-16 w-16 text-muted-foreground/30 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-foreground mb-3">{t.notFounder}</h1>
          <Link to="/dashboard">
            <Button variant="outline" className="gap-2 mt-4">
              <ArrowLeft className="h-4 w-4" />
              {t.backToDashboard}
            </Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <AuthenticatedHeader />
      <main className="flex-1 container py-8 md:py-12 max-w-3xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center h-12 w-12 rounded-2xl bg-primary/10 border border-primary/20">
              <Lightbulb className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t.pageTitle}</h1>
              <p className="text-sm text-muted-foreground">{t.pageDesc}</p>
            </div>
          </div>
        </motion.div>

        {/* Submission Form */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className="border-primary/10 shadow-soft mb-10">
            <CardContent className="p-6 md:p-8 space-y-5">
              {/* Category */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">{t.categoryLabel}</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(catLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Title */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">{t.titleLabel}</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t.titlePlaceholder}
                  className="rounded-xl"
                  maxLength={120}
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">{t.descLabel}</label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t.descPlaceholder}
                  className="rounded-xl min-h-[160px]"
                  maxLength={2000}
                />
              </div>

              {/* Photo Upload */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">{t.photosLabel}</label>
                <p className="text-xs text-muted-foreground mb-3">{t.photosHint}</p>
                
                <div className="flex flex-wrap gap-3">
                  {photoPreviewUrls.map((url, i) => (
                    <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-border/50 group">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => handlePhotoRemove(i)}
                        className="absolute top-1 right-1 bg-background/80 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3.5 w-3.5 text-destructive" />
                      </button>
                    </div>
                  ))}
                  
                  {photos.length < 3 && (
                    <label className="flex items-center justify-center w-24 h-24 rounded-xl border-2 border-dashed border-border/50 cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-all">
                      <ImagePlus className="h-6 w-6 text-muted-foreground" />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handlePhotoAdd}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Submit */}
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !title.trim() || !description.trim()}
                className="w-full gap-2 rounded-xl"
                size="lg"
              >
                <Send className="h-4 w-4" />
                {isSubmitting ? t.submitting : t.submit}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Previous Ideas */}
        {suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-lg font-bold text-foreground mb-4">{t.previousIdeas}</h2>
            <div className="space-y-3">
              {suggestions.map((s) => (
                <Card key={s.id} className="border-border/30">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-foreground text-sm">{s.title}</h4>
                        <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>
                      </div>
                      <Badge variant={statusVariant(s.status)} className="shrink-0 gap-1 text-[11px]">
                        {statusIcon(s.status)}
                        {statLabels[s.status] || s.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span>{catLabels[s.category] || s.category}</span>
                      <span>·</span>
                      <span>{new Date(s.created_at).toLocaleDateString()}</span>
                    </div>
                    {s.admin_response && (
                      <div className="mt-3 p-3 bg-primary/5 rounded-lg border border-primary/10">
                        <p className="text-xs font-medium text-primary mb-1">{t.adminResponse}</p>
                        <p className="text-xs text-foreground">{s.admin_response}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
}
